export interface TextMessageSession {
    id: string;
    seq: number;
    isEnded: boolean;
}

export interface TextMessageButton {
    title: string;
    payload?: { footer: string };
    url?: string;
    hide?: boolean;
}

export enum AudioPlayerAction {
    Play = 'Play',
    Stop = 'Stop',
    ClearQueue = 'ClearQueue',
}

export enum AudioPlayerPlayBehaviour {
    REPLACE_ALL = 'REPLACE_ALL',
    ENQUEUE = 'ENQUEUE',
}

export enum AudioPlayerClearQueueBehaviour {
    CLEAR_ENQUEUED = 'CLEAR_ENQUEUED',
    CLEAR_ALL = 'CLEAR_ALL',
}

interface AudioPlayerPlayMeta {
    title: string;
    sub_title: string;
    art: {
        url: string;
    };
    background_image: {
        url: string;
    };
}

export interface AudioPlayerActionPlayData {
    action: AudioPlayerAction.Play;
    behavior: AudioPlayerPlayBehaviour;
    item: {
        stream: {
            url: string;
            offset_ms: number;
            token: string;
            expected_previous_token?: string;
        };
        metadata?: Partial<AudioPlayerPlayMeta>;
    };
}

export interface AudioPlayerActionStopData {
    action: AudioPlayerAction.Stop;
}

export interface AudioPlayerActionClearQueueData {
    action: AudioPlayerAction.ClearQueue;
    behavior: AudioPlayerClearQueueBehaviour;
}

export type AudioPlayerActionData =
    | AudioPlayerActionPlayData
    | AudioPlayerActionStopData
    | AudioPlayerActionClearQueueData;

export interface TextMessage {
    session: TextMessageSession;
    text: string;
    buttons?: TextMessageButton[];
    history?: {
        request: JSON;
        response_raw: string | null;
    };
    layout: MessageLayout;
    card?: MessageCard;
    isLoggedIn: boolean;
    audioPlayerAction?: AudioPlayerActionData;
}

export enum CardType {
    BigImage = 'BigImage',
    ItemsList = 'ItemsList',
    ImageGallery = 'ImageGallery',
}

type TCardData = DivCardBlock['image'] &
    TextCard & {
        footer: { button: Button; text: string };
        button: Button;
        title: string;
        description?: string;
        header?: { text: string };
    };

export interface MessageCard {
    card_template: CardType;
    data: TCardData & { items: TCardData[] };
}

export interface ErrorMessage {
    error: string;
    history?: {
        request: JSON;
        response_raw: string | null;
    };
}

export const isErrorMessage = (message: Message): message is ErrorMessage => {
    return 'error' in message;
};

export const isAudioPlayerMessage = (
    message: Message,
): message is TextMessage & { audioPlayerAction: AudioPlayerActionData } => {
    return 'audioPlayerAction' in message;
};

export type Message = TextMessage | ErrorMessage;

export interface Button {
    title: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any;
    url?: string;
    type?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export type Directive = Button & { hide: boolean };
export interface CardActionArgs {
    action: {
        type: 'button' | 'uri' | 'dialog-action';
        url?: string;
        directives: Directive[];
    };
}

export interface CardBase {
    type: string;
}

export interface TextCard extends CardBase {
    text: string;
    buttons: Array<{
        title: string;
        directives: TextMessageButton[];
    }>;
}

export interface DivCardBlock {
    type: string;
    image?: {
        image_url: string;
        ratio: number;
    };
    items?: DivCardBlock[];
    action?: object;
    size?: string;
    title?: string;
    text?: string;
}

export interface SearchBandMessage {
    messageId: string;
}

export interface TestAudioTrack {
    id: string;
    url: string;
    token: string;
    offsetSec: number;
    title?: string;
    expectedPreviousToken?: string;
    logoUrl?: string;
}

export enum AudioPlaybackState {
    PLAYING = 'PLAYING',
    FINISHED = 'FINISHED',
    STOPPED = 'STOPPED',
}

export interface AudioPlayerState {
    token: string;
    state: AudioPlaybackState;
    offset_ms: number;
}

export enum AudioPlayerEventType {
    PlaybackStarted = 'AudioPlayer.PlaybackStarted',
    PlaybackFinished = 'AudioPlayer.PlaybackFinished',
    PlaybackNearlyFinished = 'AudioPlayer.PlaybackNearlyFinished',
    PlaybackStopped = 'AudioPlayer.PlaybackStopped',
    PlaybackFailed = 'AudioPlayer.PlaybackFailed',
}

export interface AudioPlayerEvent {
    type: AudioPlayerEventType;
    error?: {
        message: string;
        type: string;
    };
}

export interface TestChatHistory {
    request: object;
    response_raw: string | null;
}

export enum PlayerControlPhrases {
    Continue = 'продолжи',
    NextTrack = 'дальше',
    PrevTrack = 'запусти предыдущий',
}

export type MessageLayout = {
    cards: CardBase[];
};
